<template>
  <v-container class="transparent" fluid>
    <bread-crumb :breadcrumb-data="breadcrumbData" />

    <v-sheet class="pa-2 my-6" rounded min-height="600" outlined>
      <v-row>
        <!-- LEFT -->
        <v-col cols="6">
          <v-sheet>
            <v-sheet
                style="background-color: mintcream"
                class="font-weight-medium pa-2"
            >
              SignatureDB Category
            </v-sheet>

            <v-card-text>
              <div>
                <v-icon color="blue" small>mdi-heart</v-icon>
                This page provides access to signature features generated by PTMax,
                which are used for enrichment analysis. Criteria:
                <br />
                1️⃣ signature contains at least 5 sites;
                <br />
                2️⃣ sites supported by at least 2 publications;
                <br />
                ✅ Most signatures satisfy both (1️⃣ + 2️⃣), while a small subset meet
                only one condition.
              </div>
            </v-card-text>

            <v-row dense>
              <v-col
                  v-for="(item, key) in geneSetMap"
                  :key="key"
                  cols="12"
              >
                <v-card
                    outlined
                    class="pa-3 gene-set-name clickable"
                    :class="{ 'active-card': activeCategory === key }"
                    @click="setActiveCategory(key)"
                >
                  <v-row no-gutters align="center">
                    <v-col cols="8">
                      {{ item.title }}
                    </v-col>
                    <v-col cols="4" class="text-right">
                      <v-btn
                          x-small
                          outlined
                          color="primary"
                          @click.stop="downloadGMT(item)"
                      >
                        <v-icon left x-small>mdi-download</v-icon>
                        Download
                      </v-btn>
                    </v-col>
                  </v-row>

                  <div class="gene-set-desc mt-2">
                    {{ item.desc }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-sheet>
        </v-col>

        <!-- RIGHT -->
        <v-col cols="6">
          <v-sheet outlined class="pa-4">
            <div
                v-if="activeCategoryInfo"
                class="text-h6 font-weight-bold mb-2"
            >
              {{ activeCategoryInfo.title }}
            </div>

            <div
                v-if="activeFeatureCount"
                class="text-body-2"
            >
              Number of terms:
              <strong>{{ activeFeatureCount.signature }}</strong>
            </div>

            <div
                v-if="activeFeatureCount"
                class="text-body-2"
            >
              Number of annotating sites:
              <strong>{{ activeFeatureCount.site_seq_count }}</strong>
            </div>

            <v-divider class="my-4" />

            <v-text-field
                v-model="termSearch"
                dense
                outlined
                clearable
                hide-details
                placeholder="Search signature terms..."
                class="my-3"
            />

            <v-sheet class="term-panel pa-3">
              <div class="term-grid">
                <router-link
                    v-for="item in activeTerms"
                    :key="item.signature_name"
                    :to="{
                    path: '/signature',
                    query: { name: item.signature_name }
                  }"
                    target="_blank"
                    class="signature-link"
                >
                  {{ item.original_name }}
                </router-link>

                <div
                    v-if="!activeTerms.length"
                    class="text-caption grey--text"
                >
                  No signature terms available.
                </div>
              </div>
            </v-sheet>
          </v-sheet>
        </v-col>
      </v-row>
    </v-sheet>
  </v-container>
</template>

<script>
import BreadCrumb from "@/components/sub/breadcrumb.vue";
import axios from "axios";
import { statisticdata } from '@/config/exampleList'
import {apiprefix} from "@/main";

export default {
  name: "SignatureDB",
  components: { BreadCrumb },

  data() {
    return {
      activeCategory: "HallmarkProcess",
      termSearch: "",

      // feature_count: {
      //   CancerHallmark: { signature: 10, site_seq_count: 1596 },
      //   Figure: { signature: 282, site_seq_count: 1921 },
      //   CoGene: { signature: 1063, site_seq_count: 5521 },
      //   CoDisease: { signature: 531, site_seq_count: 2921 },
      //   CoChemical: { signature: 479, site_seq_count: 2639 }
      // },

      signatureTermsByType: {},

      geneSetMap: {
        HallmarkProcess: {
          title: "HallmarkProcess",
          desc:
              "BioLinkBERT was applied to text-mining sentences describing individual PTM sites to predict cancer hallmark associations at the sentence level. Sentence-level predictions were aggregated to generate Cancer Hallmark–PTM site signatures, enabling hallmark annotation at site resolution. Signatures were filtered using the predefined criteria (1️⃣ and 2️⃣).",
          downtype: "hallmarkprocess"
        },
        Figure: {
          title: "Figure",
          desc:
              "Figure signatures were constructed by aggregating PTM sites contained within individual figures, with figure-level semantic information summarized from figure titles using GPT. Signatures were retained according to criterion 1️⃣.",
          downtype: "cofigure"
        },
        CoGene: {
          title: "CoGene",
          desc:
              "Gene co-mention signatures represent reproducible Gene–PTM site associations aggregated from sentence-level evidence at the publication level, with criteria 1️⃣ and 2️⃣ applied.",
          downtype: "cogene"
        },
        CoDisease: {
          title: "CoDisease",
          desc:
              "Disease co-mention signatures represent reproducible Disease–PTM site associations aggregated from sentence-level evidence at the publication level, with criteria 1️⃣ and 2️⃣ applied.",
          downtype: "codisease"
        },
        CoChemical: {
          title: "CoPharmacologicallyActiveChemical",
          desc:
              "Only pharmacologically active chemicals, as determined by a GPT-based classification, were retained when constructing chemical–PTM site co-mention signatures from sentence-level text-mining evidence aggregated at the publication level, with criteria 1️⃣ and 2️⃣ applied.",
          downtype: "cochemical"
        }
      }
    };
  },

  computed: {
    feature_count() {
      return statisticdata['feature_count']
    },
    breadcrumbData() {
      return [
        { text: "Home", disabled: false, to: "/" },
        { text: "SignatureDB", disabled: true }
      ];
    },

    activeCategoryInfo() {
      return this.geneSetMap[this.activeCategory] || null;
    },

    activeFeatureCount() {
      return this.feature_count[this.activeCategory] || null;
    },

    activeTerms() {
      const category = this.activeCategoryInfo;
      if (!category) return [];

      const terms =
          this.signatureTermsByType[category.downtype] || [];

      const sorted = [...terms].sort((a, b) => {
        const na = (a.original_name || "").toLowerCase();
        const nb = (b.original_name || "").toLowerCase();
        return na.localeCompare(nb);
      });

      if (!this.termSearch) return sorted;

      const q = this.termSearch.toLowerCase();
      return sorted.filter(t =>
          (t.original_name || "").toLowerCase().includes(q)
      );
    }
  },

  methods: {
    setActiveCategory(key) {
      if (this.geneSetMap[key]) {
        this.activeCategory = key;
        this.termSearch = "";
      }
    },

    async downloadGMT(item) {
      const res = await fetch(
          `${apiprefix}/api/get_download_file?file_type=${item.downtype}&t=${Date.now()}`
      );

      const blob = await res.blob();

      const contentDisposition = res.headers.get("Content-Disposition");
      let filename = "download.gmt";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  },

  mounted() {
    axios
        .get(`${apiprefix}/api/signature_terms`)
        .then(res => {
          if (res.data?.status && res.data.data) {
            this.signatureTermsByType = res.data.data;
          } else {
            console.warn("Invalid signature_terms response");
            this.signatureTermsByType = {};
          }
        })
        .catch(err => {
          console.error("Failed to fetch signature terms:", err);
          this.signatureTermsByType = {};
        });
  }
};
</script>

<style scoped lang="scss">
.gene-set-name {
  font-weight: bold;
  color: #8b0000;
}

.gene-set-desc {
  color: #0d47a1;
  font-size: 0.9rem;
  line-height: 1.4;
}

.clickable {
  cursor: pointer;
}

.active-card {
  border: 2px solid #1976d2;
  background-color: #f5faff;
}

.term-panel {
  height: 520px;
  overflow-y: auto;
}

.term-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
}

.signature-link {
  white-space: nowrap;
  text-decoration: underline;
  font-size: 0.85rem;
  color: #1976d2;
}
</style>
